import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ZoomIn, ZoomOut, Maximize2, X, ExternalLink } from 'lucide-react';
import { abrirDocumento } from './TodosProjetos';

// =====================================================================
// PLENARIUM:LEITOR_PDF -- le o documento (PDF ou imagem) DENTRO da tela,
// sem abrir outra aba. Funciona no navegador e no aplicativo (Android
// nao mostra PDF dentro da pagina; por isso as paginas sao desenhadas
// com o pdf.js). Letra maior / menor e tela cheia para ler em voz alta.
//   <LeitorPDF url="/api/projetos-camara/anexo/ID" titulo="Ata ..." />
// A rota devolve { arquivo } (data:...;base64 ou endereco).
// =====================================================================

const CSS = `
.lpdf { border: 1px solid #e2e8f0; border-radius: 14px; background: #f1f5f9; overflow: hidden; margin-top: 12px; }
.lpdf-barra { display: flex; align-items: center; gap: 6px; padding: 8px; background: #fff; border-bottom: 1px solid #e2e8f0; flex-wrap: wrap; }
.lpdf-barra b { flex: 1; min-width: 120px; font-size: .95rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: #0f172a; }
.lpdf-bt { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 42px; min-width: 42px; padding: 0 12px; border-radius: 10px; border: 1px solid #cbd5e1; background: #fff; color: #0f172a; font: inherit; font-weight: 700; font-size: .9rem; cursor: pointer; }
.lpdf-bt[disabled] { opacity: .4; cursor: default; }
.lpdf-corpo { max-height: 68vh; overflow: auto; padding: 10px; -webkit-overflow-scrolling: touch; }
.lpdf-pags { display: flex; flex-direction: column; align-items: center; gap: 10px; }
.lpdf-pags canvas, .lpdf-pags img { background: #fff; box-shadow: 0 2px 10px rgba(15,23,42,.15); border-radius: 4px; max-width: none; display: block; }
.lpdf-msg { padding: 28px 16px; text-align: center; color: #64748b; }
.lpdf.cheia { position: fixed; inset: 0; z-index: 9500; margin: 0; border-radius: 0; border: 0; display: flex; flex-direction: column; background: #334155; }
.lpdf.cheia .lpdf-barra { background: #0f172a; border-color: #0f172a; padding: calc(8px + env(safe-area-inset-top)) 10px 8px; }
.lpdf.cheia .lpdf-barra b { color: #fff; }
.lpdf.cheia .lpdf-corpo { flex: 1; max-height: none; }
`;

let pdfjsPromessa = null;
function carregarPdfjs() {
  if (!pdfjsPromessa) {
    pdfjsPromessa = import('pdfjs-dist/legacy/build/pdf').then(m => {
      const lib = m.default && m.default.getDocument ? m.default : m;
      lib.GlobalWorkerOptions.workerSrc = (process.env.PUBLIC_URL || '') + '/pdf.worker.min.js';
      return lib;
    }).catch(e => { pdfjsPromessa = null; throw e; });
  }
  return pdfjsPromessa;
}

const limpar = (el) => { if (el) while (el.firstChild) el.removeChild(el.firstChild); };

function dataUrlParaBytes(dados) {
  const [cab, corpo] = dados.split(',');
  const tipo = (cab.match(/:(.*?);/) || [])[1] || 'application/pdf';
  const bin = atob(corpo); const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return { tipo, bytes };
}

export default function LeitorPDF({ url, titulo }) {
  const [estado, setEstado] = useState({ fase: 'carregando' }); // carregando | pdf | imagem | outro | erro
  const [zoom, setZoom] = useState(1);
  const [cheia, setCheia] = useState(false);
  const [largura, setLargura] = useState(0);
  const corpoRef = useRef(null);
  const pagsRef = useRef(null);
  const docRef = useRef(null);
  const geracao = useRef(0);

  // 1) busca o documento
  useEffect(() => {
    let vivo = true;
    setEstado({ fase: 'carregando' }); setZoom(1);
    geracao.current++; limpar(pagsRef.current);
    (async () => {
      try {
        const r = await axios.get(url);
        const dados = r.data && r.data.arquivo;
        if (!dados) { if (vivo) setEstado({ fase: 'erro', msg: 'Documento sem arquivo.' }); return; }
        let tipo, fonte;
        if (dados.startsWith('data:')) { const d = dataUrlParaBytes(dados); tipo = d.tipo; fonte = d.bytes; }
        else { tipo = /\.(png|jpe?g|webp|gif)(\?|$)/i.test(dados) ? 'image/*' : 'application/pdf'; fonte = dados; }
        if (tipo.startsWith('image/')) {
          const src = typeof fonte === 'string' ? fonte : URL.createObjectURL(new Blob([fonte], { type: tipo }));
          if (vivo) setEstado({ fase: 'imagem', src });
          return;
        }
        if (tipo !== 'application/pdf') { if (vivo) setEstado({ fase: 'outro' }); return; }
        const lib = await carregarPdfjs();
        const doc = await lib.getDocument({ ...(typeof fonte === 'string' ? { url: fonte } : { data: fonte }), isEvalSupported: false /* PLENARIUM:PDF_SEGURO -- PDF malicioso nao executa codigo (CVE-2024-4367) */ }).promise;
        if (!vivo) { doc.destroy(); return; }
        if (docRef.current) { try { docRef.current.destroy(); } catch (e) { /* segue */ } }
        docRef.current = doc;
        setEstado({ fase: 'pdf', paginas: doc.numPages });
      } catch (e) {
        if (vivo) setEstado({ fase: 'erro', msg: (e && e.response && e.response.data && e.response.data.error) || 'Não foi possível abrir o documento aqui.' });
      }
    })();
    return () => { vivo = false; };
  }, [url]);
  useEffect(() => () => { if (docRef.current) { try { docRef.current.destroy(); } catch (e) { /* segue */ } } }, []);

  // 2) acompanha a largura da area de leitura
  const medir = useCallback(() => { if (corpoRef.current) setLargura(corpoRef.current.clientWidth - 20); }, []);
  useEffect(() => {
    medir();
    let t = null;
    const aoMudar = () => { clearTimeout(t); t = setTimeout(medir, 150); };
    window.addEventListener('resize', aoMudar);
    return () => { window.removeEventListener('resize', aoMudar); clearTimeout(t); };
  }, [medir, cheia, estado.fase]);

  // 3) desenha as paginas
  useEffect(() => {
    if (estado.fase !== 'pdf' || !docRef.current || !pagsRef.current || largura < 50) return;
    const minha = ++geracao.current;
    const doc = docRef.current; const caixa = pagsRef.current;
    const alvo = Math.round(Math.min(largura, 1100) * zoom);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    (async () => {
      for (let n = 1; n <= doc.numPages; n++) {
        if (minha !== geracao.current) return;
        const pag = await doc.getPage(n);
        const v1 = pag.getViewport({ scale: 1 });
        const escala = alvo / v1.width;
        const vp = pag.getViewport({ scale: escala * dpr });
        const c = document.createElement('canvas');
        c.width = Math.floor(vp.width); c.height = Math.floor(vp.height);
        c.style.width = Math.floor(vp.width / dpr) + 'px'; c.style.height = Math.floor(vp.height / dpr) + 'px';
        await pag.render({ canvasContext: c.getContext('2d'), viewport: vp }).promise;
        if (minha !== geracao.current) return;
        if (n === 1) limpar(caixa);
        caixa.appendChild(c);
      }
    })().catch(() => { if (minha === geracao.current) setEstado({ fase: 'erro', msg: 'Não foi possível desenhar o documento.' }); });
  }, [estado.fase, largura, zoom]);

  // tela cheia: Esc fecha
  useEffect(() => {
    if (!cheia) return undefined;
    const tecla = (e) => { if (e.key === 'Escape') setCheia(false); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [cheia]);

  const podeZoom = estado.fase === 'pdf' || estado.fase === 'imagem';
  return (
    <div className={`lpdf ${cheia ? 'cheia' : ''}`}>
      <style>{CSS}</style>
      <div className="lpdf-barra">
        <b>{titulo || 'Documento'}{estado.fase === 'pdf' ? ` · ${estado.paginas} pág.` : ''}</b>
        <button type="button" className="lpdf-bt" title="Letra menor" disabled={!podeZoom || zoom <= 0.6} onClick={() => setZoom(z => Math.max(0.6, +(z - 0.2).toFixed(1)))}><ZoomOut size={18} /></button>
        <button type="button" className="lpdf-bt" title="Letra maior" disabled={!podeZoom || zoom >= 2.4} onClick={() => setZoom(z => Math.min(2.4, +(z + 0.2).toFixed(1)))}><ZoomIn size={18} /></button>
        {cheia
          ? <button type="button" className="lpdf-bt" onClick={() => setCheia(false)}><X size={18} /> Fechar</button>
          : <button type="button" className="lpdf-bt" onClick={() => setCheia(true)}><Maximize2 size={18} /> Tela cheia</button>}
      </div>
      <div className="lpdf-corpo" ref={corpoRef}>
        {estado.fase === 'carregando' && <div className="lpdf-msg">Carregando o documento…</div>}
        {estado.fase === 'erro' && <div className="lpdf-msg">{estado.msg}<br /><button type="button" className="lpdf-bt" style={{ marginTop: 10 }} onClick={() => abrirDocumento(url)}><ExternalLink size={16} /> Abrir em outra aba</button></div>}
        {estado.fase === 'outro' && <div className="lpdf-msg">Este tipo de arquivo não abre dentro da tela.<br /><button type="button" className="lpdf-bt" style={{ marginTop: 10 }} onClick={() => abrirDocumento(url)}><ExternalLink size={16} /> Abrir o arquivo</button></div>}
        {estado.fase === 'imagem' && <div className="lpdf-pags"><img src={estado.src} alt={titulo || 'Documento'} style={{ width: largura > 50 ? Math.round(Math.min(largura, 1100) * zoom) : '100%' }} /></div>}
        <div className="lpdf-pags" ref={pagsRef} style={{ display: estado.fase === 'pdf' ? 'flex' : 'none' }} />
      </div>
    </div>
  );
}
